import { useEffect, useMemo, useRef, useState } from 'react'
import { CanonicalLoad } from '@numeo/types-core'
import { fmtMiles } from '../utils/format'

interface PlaceBidCalculatorProps {
    load: CanonicalLoad
    /** Emits the broker price — the number that drives the email template's rate. null when cleared. */
    onBidChange: (brokerPrice: number | null) => void
    /** The CHECKED nearby driver's own bid on this load (null when no driver is picked or they haven't bid).
     *  When present it seeds "Price for Driver" — the operator's cost side starts at what the driver asked. */
    driverBidPrice?: number | null
    /** Identity of the checked driver (their id) — distinguishes "operator checked a DIFFERENT driver"
     *  (always re-seed) from "the SAME driver re-bid live" (re-seed only if the operator hasn't manually
     *  edited the driver price since the last seed). */
    driverBidKey?: string
}

const num = (v: string): number | null => {
    const parsed = parseFloat(v)
    return Number.isFinite(parsed) ? parsed : null
}
const money = (v: number): string => v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/**
 * Place Bid calculator (mirrors the reference "Bid Placement" panel). Two linked pairs — Price for Broker ↔
 * its RPM, Price for Driver ↔ its RPM — with a live Revenue readout.
 *
 * Each price and its RPM are their OWN state, kept in sync: editing one recomputes ONLY its sibling (price
 * ÷ miles, or rpm × miles, to 2 decimals). The field you're typing keeps your raw keystrokes — so "1.09"
 * into an RPM stays "1.09" while the price becomes 1.09 × miles, instead of the old derive-then-round-trip
 * that fought every keystroke and dropped the cents. Revenue = Broker − Driver; % = Revenue ÷ Broker. The
 * broker price is emitted upward so the email template's rate tracks it.
 */
export const PlaceBidCalculator = ({ load, onBidChange, driverBidPrice, driverBidKey }: PlaceBidCalculatorProps) => {
    const miles = useMemo(() => (load.distanceMiles ? Math.round(Number(load.distanceMiles)) : 0), [load.distanceMiles])

    // Seed the broker price (and its RPM) from the load's existing rate, if any. Driver starts empty.
    const seedRate = load.rateAmountUsd != null ? Number(load.rateAmountUsd) : null
    const [brokerPrice, setBrokerPrice] = useState<string>(() => (seedRate != null ? String(seedRate) : ''))
    const [brokerRpm, setBrokerRpm] = useState<string>(() => (seedRate != null && miles > 0 ? (seedRate / miles).toFixed(2) : ''))
    const [driverPrice, setDriverPrice] = useState<string>('')
    const [driverRpm, setDriverRpm] = useState<string>('')

    const brokerNum = num(brokerPrice)
    const driverNum = num(driverPrice)
    const revenue = brokerNum != null ? brokerNum - (driverNum ?? 0) : null
    const revenuePct = brokerNum && brokerNum > 0 && revenue != null ? (revenue / brokerNum) * 100 : 0

    // Checking a driver who has BID this load seeds Price for Driver with their asking price (+ its RPM).
    // Overwrite rules: checking a DIFFERENT driver always re-seeds (explicit operator action); the SAME
    // driver re-bidding live re-seeds only while the operator hasn't manually edited the driver price since
    // the last seed (their in-progress entry wins). Picking a no-bid driver / unchecking never touches it.
    const driverPriceTouched = useRef(false)
    const lastSeedKey = useRef<string | undefined>(undefined)
    useEffect(() => {
        if (driverBidPrice == null) return
        if (driverBidKey !== lastSeedKey.current) {
            lastSeedKey.current = driverBidKey
            driverPriceTouched.current = false
        } else if (driverPriceTouched.current) {
            return
        }
        setDriverPrice(String(driverBidPrice))
        setDriverRpm(miles > 0 ? (driverBidPrice / miles).toFixed(2) : '')
    }, [driverBidPrice, driverBidKey, miles])

    // Price edit → recompute the paired RPM (price ÷ miles); keep the price field's raw keystrokes.
    const onPrice = (setPrice: (v: string) => void, setRpm: (v: string) => void) => (raw: string) => {
        setPrice(raw)
        const p = num(raw)
        setRpm(p != null && miles > 0 ? (p / miles).toFixed(2) : '')
    }
    // RPM edit → recompute the paired price (rpm × miles); keep the RPM field's raw keystrokes.
    const onRpm = (setRpm: (v: string) => void, setPrice: (v: string) => void) => (raw: string) => {
        setRpm(raw)
        const r = num(raw)
        setPrice(r != null && miles > 0 ? (r * miles).toFixed(2) : '')
    }
    // Broker-side changes must reach the compose in the SAME input event. Relaying them through an
    // effect left the Place Bid action briefly wired to the previous email body (`$-`).
    const onBrokerPrice = (raw: string) => {
        onPrice(setBrokerPrice, setBrokerRpm)(raw)
        onBidChange(num(raw))
    }
    const onBrokerRpm = (raw: string) => {
        onRpm(setBrokerRpm, setBrokerPrice)(raw)
        const rpm = num(raw)
        onBidChange(rpm != null && miles > 0 ? Number((rpm * miles).toFixed(2)) : null)
    }
    // Driver-side edits additionally mark the pair as operator-owned (see the seed effect above).
    const onDriverPrice = (raw: string) => {
        driverPriceTouched.current = true
        onPrice(setDriverPrice, setDriverRpm)(raw)
    }
    const onDriverRpm = (raw: string) => {
        driverPriceTouched.current = true
        onRpm(setDriverRpm, setDriverPrice)(raw)
    }

    return (
        <div className="rounded-xl border border-border bg-background p-3.5">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">Place Bid</span>
                <span className="text-[11px] text-foreground-muted">{fmtMiles(load.distanceMiles)}</span>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
                <Field label="Price for Broker" value={brokerPrice} onChange={onBrokerPrice} prefix="$" />
                <Field label="Price for Driver" value={driverPrice} onChange={onDriverPrice} prefix="$" />
                <Field label="Price / Mile" value={brokerRpm} onChange={onBrokerRpm} prefix="$" disabled={miles === 0} />
                <Field label="Price / Mile (driver)" value={driverRpm} onChange={onDriverRpm} prefix="$" disabled={miles === 0} />
            </div>

            <div className="mt-3 flex items-center justify-between rounded-lg bg-surface px-3 py-2">
                <span className="text-xs font-medium text-foreground-muted">Revenue</span>
                <span className="text-sm font-semibold text-success">
                    ${revenue != null ? money(revenue) : '0.00'} <span className="text-xs font-medium text-foreground-muted">({revenuePct.toFixed(2)}%)</span>
                </span>
            </div>
        </div>
    )
}

const Field = ({
    label,
    value,
    onChange,
    prefix,
    disabled,
}: {
    label: string
    value: string
    onChange: (v: string) => void
    prefix?: string
    disabled?: boolean
}) => (
    <label className="block">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">{label}</span>
        <div
            className={`flex h-8 items-center gap-1 rounded-xl bg-surface px-2 shadow-field transition-shadow duration-150 focus-within:shadow-none focus-within:ring-2 focus-within:ring-accent ${
                disabled ? 'opacity-50' : ''
            }`}
        >
            {prefix && <span className="text-xs text-foreground-muted">{prefix}</span>}
            <input
                type="number"
                inputMode="decimal"
                aria-label={label}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder="0"
                className="w-full min-w-0 border-none bg-transparent text-sm text-foreground outline-none placeholder:text-foreground-muted"
            />
        </div>
    </label>
)
